import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg } from "@/components/email/EmParts";

const METHOD = { verified_email: "Account email", guest_email: "Guest checkout email", history_email: "Email (historical order)", wc_customer_id: "WooCommerce customer ID", staff: "Staff decision" };
const STATES = [["ambiguous", "Ambiguous — needs review"], ["unmatched", "Unmatched"], ["matched", "Matched"]];
const money = (v) => `£${Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

function Summary({ s }) {
  const box = (l, v, id) => <div key={l} className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={id}><p className="text-[11px] uppercase tracking-wide text-slate-500">{l}</p><p className="text-2xl font-semibold text-slate-900">{v}</p></div>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {box("Orders checked", s.total, "wm-total")}{box("Matched", s.by_state.matched || 0, "wm-matched")}
        {box("Ambiguous", s.by_state.ambiguous || 0, "wm-ambiguous")}{box("Unmatched", s.by_state.unmatched || 0, "wm-unmatched")}
        {box("Customers matched", s.customers_matched, "wm-customers")}
      </div>
      <p className="text-sm text-slate-600" data-testid="wm-methods">Matched by: {Object.entries(s.by_method).map(([k, v]) => `${METHOD[k] || k} ${v}`).join(" · ") || "—"}
        {" · "}matched order value {money(s.matched_value)}{s.earliest_order ? ` · history from ${s.earliest_order.slice(0, 10)}` : ""}</p>
    </div>
  );
}

function Row({ o, canManage, onDone }) {
  const [c, setC] = useState("");
  const resolve = async (contact) => {
    try { await api.post(`/email-marketing/woo-matching/orders/${o.wc_order_id}/resolve`, { contact_legacy_id: contact }); toast.success(contact ? "Linked to customer" : "Marked as no customer"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`wm-row-${o.wc_order_id}`}>
      <td className="py-2 pr-3 whitespace-nowrap">#{o.wc_number}<span className="block text-[11px] text-slate-400">{(o.date || "").slice(0, 10)} · {o.status}</span></td>
      <td className="py-2 pr-3">{o.name || "—"}<span className="block text-[11px] text-slate-400">{o.email_masked || "no email"}{o.postcode ? ` · ${o.postcode}` : ""}{o.guest ? " · guest" : ""}</span></td>
      <td className="py-2 pr-3 tabular-nums">{money(o.total)}</td>
      <td className="py-2 pr-3">{o.state === "matched" ? <span>#{o.contact_legacy_id} {o.contact_name}<span className="block text-[11px] text-slate-400">{METHOD[o.method] || o.method}</span></span>
        : (o.candidates || []).map((x) => (
          <div key={x.contact_legacy_id} className="flex flex-wrap items-center gap-2 mb-1"><span className="text-sm">#{x.contact_legacy_id} {x.name}<span className="block text-[11px] text-slate-400">{x.reason}</span></span>
            {canManage && <Button size="sm" variant="outline" className="h-7" onClick={() => resolve(x.contact_legacy_id)} data-testid={`wm-pick-${o.wc_order_id}-${x.contact_legacy_id}`}>This customer</Button>}</div>))}</td>
      <td className="py-2 text-right whitespace-nowrap">{canManage && <div className="flex justify-end gap-1">
        <Input value={c} onChange={(e) => setC(e.target.value)} placeholder="Customer #" className="h-8 w-28" data-testid={`wm-input-${o.wc_order_id}`} />
        <Button size="sm" className="h-8" disabled={!c.trim()} onClick={() => resolve(c.trim())} data-testid={`wm-link-${o.wc_order_id}`}>Link</Button>
        {o.state !== "unmatched" && <Button size="sm" variant="ghost" className="h-8" onClick={() => resolve(null)} data-testid={`wm-none-${o.wc_order_id}`}>No match</Button>}</div>}</td>
    </tr>
  );
}

function Orders({ canManage, onChanged }) {
  const [state, setState] = useState("ambiguous");
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.get("/email-marketing/woo-matching/orders", { params: { state, q, skip, limit: 50 } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [state, q, skip]);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onChanged(); };
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3" data-testid="wm-orders">
      <div className="flex flex-wrap gap-2 items-center">
        {STATES.map(([k, l]) => <Button key={k} size="sm" variant={state === k ? "default" : "outline"} onClick={() => { setState(k); setSkip(0); }} data-testid={`wm-tab-${k}`}>{l}</Button>)}
        <Input value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} placeholder="Search name, order # or postcode" className="h-9 w-64 ml-auto" data-testid="wm-search" />
      </div>
      {d && <><div className="overflow-x-auto"><table className="w-full min-w-[820px] text-sm" data-testid="wm-table"><thead><tr className="text-left text-xs text-slate-500">
        <th className="pb-2 font-medium">Order</th><th className="pb-2 font-medium">Billing</th><th className="pb-2 font-medium">Total</th><th className="pb-2 font-medium">{state === "matched" ? "Customer" : "Possible customers"}</th><th /></tr></thead>
        <tbody>{d.items.map((o) => <Row key={o.wc_order_id} o={o} canManage={canManage} onDone={done} />)}</tbody></table></div>
        {d.items.length === 0 && <p className="text-sm text-slate-400" data-testid="wm-empty">Nothing here.</p>}
        <div className="flex items-center gap-2 text-sm text-slate-500"><span className="mr-auto" data-testid="wm-count">{d.total} orders</span>
          <Button size="sm" variant="outline" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - 50))} data-testid="wm-prev">Previous</Button>
          <Button size="sm" variant="outline" disabled={skip + 50 >= d.total} onClick={() => setSkip(skip + 50)} data-testid="wm-next">Next</Button></div></>}
    </section>
  );
}

export default function EmWooMatching() {
  const { can } = useAuth();
  const canManage = can("email_marketing.manage");
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(0);
  const load = useCallback(() => { api.get("/email-marketing/woo-matching").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/email-marketing/woo-matching/run"); setS(r.data); setV(v + 1); toast.success(`${r.data.orders} orders checked`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="em-woo-matching">
      <Link to="/email-marketing/automations" className="text-xs text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" />Automations</Link>
      <section className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-wrap items-center gap-3">
        <div className="mr-auto max-w-3xl"><h2 className="text-base font-bold text-slate-900">WooCommerce customer matching</h2>
          <p className="text-sm text-slate-500">Links web orders (synced and historical) to TedOps customers by WooCommerce customer ID, verified email and guest checkout details. Read-only: orders, jobs, stock, refunds and customer records are never changed, and ambiguous orders are never linked or merged automatically.</p>
          {s?.last_run && <p className="text-xs text-slate-400 mt-1" data-testid="wm-last-run">Last checked {new Date(s.last_run).toLocaleString("en-GB")}</p>}</div>
        {canManage && <Button onClick={run} disabled={busy} data-testid="wm-run">{busy ? "Checking…" : s?.last_run ? "Re-check orders" : "Match orders"}</Button>}
      </section>
      {s && s.total > 0 && <Summary s={s} />}
      {s && s.total > 0 && <Orders key={v} canManage={canManage} onChanged={load} />}
    </div>
  );
}
